// Size guard of the tool (todo/20, U5): sizes of a dataset and of an "Optimize" run, the rules that warn before a
// long run, and a time estimate for the local search. No DOM. Nothing here limits a run; the analyst can start
// anyway and cancel.
//
// Thresholds (results/experiments/scaling, results/experiments/mushroom, results/scaling/titanic_full.md in the
// parent repository): one attribute order of titanic-full (7 attributes, about 100,000 ribbon pairs) takes up to
// 22 s (Multistart Local Search) and up to 102 s (Pairwise Branch-and-Bound), and 720 of them minutes to hours; the
// branch-and-bound based methods list all category orders of an attribute and do not finish with several attributes
// of 9 categories (out of memory at 12); Overlap and Crossing Angle visit every ribbon pair in every evaluation.
import { factorial } from './enumeration/config-id.js';
import { buildStructure } from './optimizers/constraints.js';
import { localSearch } from './optimizers/local-search.js';
import { Problem } from './optimizers/problem.js';
import { rng } from './optimizers/rng.js';
import { TOOL_DEFAULTS } from './optimizers/tool-defaults.js';

export const LIMITS = {
    seconds: 30,                // estimated run time (decision of todo/20)
    attributeOrders: 720,       // from this number on: 7 attributes with a fixed first attribute, 6 without
    categoryOrders: 5040,       // 7 categories without a constraint
    ribbonPairs: 500000,        // pairs of ribbons of the same band, upper bound
    estimatePairs: 5000000,     // above: no calibration run for the pair-based metrics (setup alone is slow)
    drawRibbons: 5000,          // ribbons of the shown configuration before the plot asks
    fileBytes: 20 * 2 ** 20,    // file size before the file is read
};
const PAIR_METRICS = ['overlap', 'crossingAngle'];
const BRANCH_AND_BOUND = ['crossingsBnb', 'overlapSurrogate', 'overlapBnb'];

/**
 * Sizes of a dataset in a configuration ([{ name, categories }], attribute order as shown).
 * @returns { rows, attributes, categories: [n], ribbons, ribbonsPerBand: [n], ribbonPairs (pairs of ribbons of the
 *            same band, an upper bound of the pairs that can cross) }
 */
export function sizeInfo(dataset, config) {
    const names = config.map(a => a.name);
    const seen = names.slice(1).map(() => new Set());
    for (const row of dataset.rows) {
        let key = String(row[names[0]]);
        for (let k = 1; k < names.length; k++) {
            key += '\u0000' + row[names[k]];
            seen[k - 1].add(key);
        }
    }
    const ribbonsPerBand = seen.map(s => s.size);
    return {
        rows: dataset.rows.length, attributes: config.length, categories: config.map(a => a.categories.length),
        ribbons: ribbonsPerBand.reduce((a, b) => a + b, 0), ribbonsPerBand,
        ribbonPairs: ribbonsPerBand.reduce((a, r) => a + r * (r - 1) / 2, 0),
    };
}

/**
 * Sizes of an "Optimize" run.
 * @param constraints  request field of optimizers/optimize.js ({ positions, categories }), already with the preset
 *                     of the mode; mode 'entropyFirst' fixes one more attribute
 * @param optimizer    optimizer of the run (default: the one of the metric in TOOL_DEFAULTS)
 * @returns sizeInfo plus { metric, optimizer, attributeOrders, categoryOrders: [{ name, orders }] (admissible category
 *          orders per attribute, an upper bound if blocks and pins are combined), largestCategoryOrders }
 */
export function runSize(dataset, config, constraints, metric, mode = 'unconstrained', optimizer = null) {
    const positions = Object.keys((constraints && constraints.positions) || {}).length;
    const fixed = Math.min(config.length, positions + (mode === 'entropyFirst' && positions === 0 ? 1 : 0));
    const categoryOrders = config.map(a => {
        const structure = buildStructure(a.categories, ((constraints && constraints.categories) || {})[a.name]);
        return { name: a.name, orders: factorial(structure ? structure.freeUnits.length : a.categories.length) };
    });
    return {
        ...sizeInfo(dataset, config), metric, optimizer: optimizer || TOOL_DEFAULTS.metrics[metric].optimizer,
        attributeOrders: factorial(config.length - fixed), categoryOrders,
        largestCategoryOrders: categoryOrders.reduce((a, b) => (b.orders > a.orders ? b : a)),
    };
}

// True if estimateRun gives a time for the run: local search only, and not if the setup alone is too large.
export function canEstimate(size) {
    return size.optimizer === 'multiStart' && !(PAIR_METRICS.includes(size.metric) && size.ribbonPairs > LIMITS.estimatePairs);
}

/**
 * Time estimate of a Multistart Local Search run from a few capped descents on the shown attribute order.
 * @param request  as for optimize(): { dataset, metric, config, constraints, layoutOptions }; attributeOrders = number
 *                 of attribute orders of the run (runSize)
 * @returns { seconds, atLeast (a descent hit its cap: the run takes longer), setupMs, perEvaluationMs, descentMs, evaluations }
 */
export function estimateRun({ dataset, metric, config, constraints = null, layoutOptions = {}, attributeOrders }, { budgetMs = 300, maxEvaluations = 50000 } = {}) {
    const now = () => performance.now();
    const { starts, neighborhood, strategy, seed } = TOOL_DEFAULTS.metrics[metric].options;
    const order = config.map(a => a.name);
    const t0 = now();
    const problem = new Problem({ dataset, attributeOrder: order, metric, layoutOptions, constraints: constraints && constraints.categories });
    const rand = rng(seed);
    const start = problem.randomSolution(rand);
    problem.evaluate(start);                // builds the ribbon pairs of the pair-based metrics
    const setupMs = now() - t0;
    const t1 = now();
    const probes = 5;
    for (let i = 0; i < probes; i++) problem.evaluate(problem.randomSolution(rand));
    const perEvaluationMs = Math.max((now() - t1) / probes, 1e-4);
    const cap = Math.max(20, Math.min(maxEvaluations, Math.floor(budgetMs / perEvaluationMs)));
    // Descents until the budget is used (at most four); the first one runs before the code is optimized by the
    // engine and counts only if it is the only one.
    const times = [];
    let atLeast = false, evaluations = 0;
    const t2 = now();
    for (let i = 0; i < 4 && (i === 0 || now() - t2 < budgetMs) && !atLeast; i++) {
        problem.resetStats();
        const t = now();
        const descent = localSearch(problem, i === 0 ? start : problem.randomSolution(rand), { neighborhood, strategy, maxEvaluations: cap });
        times.push(now() - t);
        evaluations += problem.evaluations;
        atLeast = descent.termination === 'budget';
    }
    const later = times.length > 1 && !atLeast ? times.slice(1) : times;
    const descentMs = later.reduce((a, b) => a + b, 0) / later.length;
    return { seconds: attributeOrders * (setupMs + starts * descentMs) / 1000, atLeast, setupMs, perEvaluationMs, descentMs, evaluations };
}

const count = n => {
    if (n < 1e15) return n.toLocaleString('en-US');
    const [mantissa, exponent] = n.toExponential(1).split('e+');
    return `${mantissa} x 10^${exponent}`;
};

export function formatDuration(seconds) {
    if (seconds < 90) return `${Math.max(1, Math.round(seconds))} s`;
    if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
    if (seconds < 48 * 3600) return `${Math.round(seconds / 3600)} h`;
    if (seconds < 730 * 86400) return `${Math.round(seconds / 86400)} days`;
    const years = seconds / (365 * 86400);
    return years < 1000 ? `${Math.round(years)} years` : 'more than 1,000 years';
}

/**
 * Reasons to warn before a run (empty: start without asking).
 * @param size      runSize
 * @param estimate  estimateRun or null
 */
export function runWarnings(size, estimate = null) {
    const reasons = [];
    if (estimate && estimate.seconds > LIMITS.seconds) {
        const duration = formatDuration(estimate.seconds);
        reasons.push(`estimated run time: ${duration.startsWith('more') ? '' : estimate.atLeast ? 'more than ' : 'about '}${duration}`);
    }
    if (size.attributeOrders >= LIMITS.attributeOrders) {
        reasons.push(`${count(size.attributeOrders)} attribute orders are optimized one after the other (fixing axis positions reduces the number of attribute orders: right-click an axis)`);
    }
    const largest = size.largestCategoryOrders;
    if (BRANCH_AND_BOUND.includes(size.optimizer) && largest.orders > LIMITS.categoryOrders) {
        reasons.push(`the method lists all ${count(largest.orders)} category orders of "${largest.name}" and is not expected to finish (more than 7 free categories)`);
    }
    if (PAIR_METRICS.includes(size.metric) && size.ribbonPairs > LIMITS.ribbonPairs) {
        reasons.push(`up to ${count(size.ribbonPairs)} pairs of ribbons are compared in every evaluation`);
    }
    return reasons;
}

// Remaining time of a run from its progress (order of orders finished after ms), in seconds.
export function remainingSeconds(order, orders, ms) {
    return order > 0 ? (ms / order) * (orders - order) / 1000 : NaN;
}
